<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import SalesPartyDocumentValidityFields from './SalesPartyDocumentValidityFields.vue';

const props = defineProps({
  documentTypes: { type: Array, default: () => [] },
  isProperty: { type: Boolean, default: false },
  label: { type: String, required: true },
  loading: { type: Boolean, default: false },
  options: { type: Array, default: () => [] },
  people: { type: Array, default: () => [] },
  section: { type: Object, required: true },
  uploading: { type: Boolean, default: false },
});
const emit = defineEmits(['update', 'upload']);
const { t } = useI18n();
const update = payload => emit('update', payload);
const selectedValues = event =>
  Array.from(event.target.selectedOptions).map(option => option.value);
const updateDocumentType = (index, documentType) => {
  const documentTypes = props.documentTypes.map((item, position) =>
    position === index ? documentType : item
  );
  update({ documentTypes });
};
const validityTypes = computed(() =>
  props.documentTypes
    .map((type, index) => ({ type, index }))
    .filter(({ type }) => type.validity_days)
);
</script>

<template>
  <article class="rounded-lais border border-lais-border-soft p-3">
    <label class="flex items-center gap-3 text-sm font-semibold text-lais-text">
      <input
        :checked="section.enabled"
        type="checkbox"
        @change="update({ enabled: $event.target.checked })"
      />
      {{ label }}
    </label>
    <LaisPrimaryButton
      v-if="section.enabled"
      class="mt-2"
      size="sm"
      variant="ghost"
      @click="update({ open: !section.open })"
    >
      {{
        section.open
          ? t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.DOCUMENTS.HIDE')
          : t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.DOCUMENTS.SHOW')
      }}
    </LaisPrimaryButton>
    <div v-if="section.enabled && section.open" class="mt-3 grid gap-3">
      <LaisField
        v-if="!isProperty"
        :label="t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.DOCUMENTS.PERSON')"
        required
      >
        <select
          :value="section.partyId"
          class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-sm"
          @change="update({ partyId: $event.target.value })"
        >
          <option value="">
            {{
              t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.DOCUMENTS.SELECT_PERSON')
            }}
          </option>
          <option v-for="party in people" :key="party.id" :value="party.id">
            {{ party.name }}
          </option>
        </select>
      </LaisField>
      <SalesPartyDocumentValidityFields
        v-for="entry in validityTypes"
        :key="entry.type.id"
        :item="entry.type"
        @update="updateDocumentType(entry.index, $event)"
      />
      <LaisField
        :label="t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.DOCUMENTS.TYPES')"
      >
        <select
          :value="section.typeIds"
          multiple
          class="min-h-20 rounded-lais border border-lais-border bg-white px-3 text-sm"
          @change="update({ typeIds: selectedValues($event) })"
        >
          <option
            v-for="type in options"
            :key="type.id"
            :value="String(type.id)"
          >
            {{ type.name || type.nome }}
          </option>
        </select>
      </LaisField>
      <LaisField
        :label="t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.DOCUMENTS.FILES')"
        required
      >
        <input
          type="file"
          multiple
          class="text-sm text-lais-text-body"
          @change="update({ files: Array.from($event.target.files || []) })"
        />
      </LaisField>
      <LaisPrimaryButton
        :disabled="
          loading ||
          uploading ||
          !section.files.length ||
          (!isProperty && !section.partyId)
        "
        :is-loading="uploading"
        @click="emit('upload')"
      >
        {{ t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.DOCUMENTS.UPLOAD') }}
      </LaisPrimaryButton>
    </div>
  </article>
</template>
